import {computed, defineComponent, onMounted, ref, watch} from "vue";
import {AllInvoicesQuery, AthumiService, type Invoice} from "@/api/enterprise-gateway-api.proxy.ts";
import {type InvoicesResponse, useInvoicingStore} from "@/app/stores/invoicing-store.ts";
import {storeToRefs} from "pinia";
import {PagedResult} from "@/app/shared/helpers/paged-result.ts";
import {SearchInvoicesQueryMapper, useSearchQueryStore} from "@/app/stores/search-query-store.ts";
import {useRoute} from "vue-router";
import AegInvoicesTable from '@/app/shared/components/aeg-invoices-table/aeg-invoices-table.vue';

export default defineComponent({
    name: 'AegInvoicesTab',
    components: {AegInvoicesTable},
    setup() {
        const invoiceStore = useInvoicingStore();
        const searchSessionQueryStore = useSearchQueryStore();
        const route = useRoute();

        const {isLoading} = storeToRefs(invoiceStore);
        const invoices = ref<InvoicesResponse>({pagedResult: PagedResult.createEmpty<Invoice>(), downloadUrl: ''});
        const searchQuery = ref<AllInvoicesQuery>(AllInvoicesQuery.fromJS({
            pageSize: 50,
            page: 1
        }));
        const defaultSearchQuery = ref<AllInvoicesQuery>();

        const toInvoicesQuery = (queryParams: Record<string, unknown>) => {
            const fromQueryParams = SearchInvoicesQueryMapper.fromQueryParams(queryParams);

            return AllInvoicesQuery.fromJS({
                fromDate: fromQueryParams.fromDate,
                toDate: fromQueryParams.toDate,
                docNr: fromQueryParams.docNr,
                service: fromQueryParams.service,
                pageSize: fromQueryParams.pageSize ?? 50,
                page: fromQueryParams.page ?? 1,
            });
        };

        const applyRouteQuery = (queryParams: Record<string, unknown>) => {
            defaultSearchQuery.value = SearchInvoicesQueryMapper.fromQueryParams(queryParams);
            searchQuery.value = toInvoicesQuery(queryParams);
        };

        const loadInvoices = async () => {
            const result = await invoiceStore.loadInvoicesForAdmin(searchQuery.value);
            if (!result) {
                return;
            }
            invoices.value = result;
        };

        const goToPage = async (page: number) => {
            searchQuery.value.page = page;
            await searchSessionQueryStore.syncUrlToInvoicesQueryIfChanged(searchQuery.value);
            await loadInvoices();
        };

        const onSearch = async (searchCriteria: AllInvoicesQuery) => {
            searchQuery.value.page = 1;
            searchCriteria.page = searchQuery.value.page;
            searchCriteria.pageSize = searchQuery.value.pageSize;
            searchQuery.value = searchCriteria;
            await loadInvoices();
            await searchSessionQueryStore.syncUrlToInvoicesQueryIfChanged(searchQuery.value);
        };

        watch(
            () => route.query,
            async (queryParams) => {
                const next = SearchInvoicesQueryMapper.fromQueryParams(queryParams as Record<string, unknown>);
                const same = SearchInvoicesQueryMapper.compareSearchSessionQueryFromParams(searchQuery.value, next);
                if (!same) {
                    applyRouteQuery(queryParams as Record<string, unknown>);
                    await loadInvoices();
                }
            }
        );

        onMounted(async () => {
            if (Object.keys(route.query).length > 0) {
                applyRouteQuery(route.query as Record<string, unknown>);
            }

            await loadInvoices();
        });

        const showDownloadLink = computed((): boolean => {
            return !!invoices.value.pagedResult && invoices.value.pagedResult.items.length > 0;
        });

        return {
            showDownloadLink,
            isLoading,
            invoices,
            defaultSearchQuery: defaultSearchQuery,
            onSearch,
            goToPage,
        };
    }
});


